// RevenueCat billing adapter — Android native only.
// The plugin is lazy-imported so the web bundle never executes native code.
// Entitlement id: "premium". Available packages are read from the current
// RevenueCat offering; product/package identifiers remain dashboard-managed.

export const packagePeriod = (pkg) => {
  const type = String(pkg.packageType || "").toUpperCase();
  if (type === "LIFETIME" || pkg.identifier === "$rc_lifetime") return "lifetime";
  if (type === "ANNUAL" || pkg.identifier === "$rc_annual") return "annual";
  if (type === "MONTHLY" || pkg.identifier === "$rc_monthly") return "monthly";
  const id = String(pkg.product?.identifier || "");
  if (/lifetime/i.test(id)) return "lifetime";
  if (/annual|year/i.test(id)) return "annual";
  if (/monthly|month/i.test(id)) return "monthly";
  return "other";
};

let Purchases = null;
let configured = false;
let configurationPromise = null;
const IDENTITY_KEY = "un_rc_identified_user_v1";

const rememberIdentity = (id) => {
  try {
    if (id) localStorage.setItem(IDENTITY_KEY, String(id));
    else localStorage.removeItem(IDENTITY_KEY);
  } catch {
    // Identity sync should never block the app.
  }
};

const hasRememberedIdentity = () => {
  try {
    return Boolean(localStorage.getItem(IDENTITY_KEY));
  } catch {
    return false;
  }
};

const load = async () => {
  if (Purchases) return;
  const mod = await import("@revenuecat/purchases-capacitor");
  Purchases = mod.Purchases;
  // Do not return the Capacitor proxy: Promise resolution treats its dynamic
  // `then` property as a thenable and initialization never completes.
};

const ensureConfigured = async () => {
  await load();
  const P = Purchases;
  if (configured) return;
  if (!configurationPromise) configurationPromise = (async () => {
  // Test Store (dev builds only): requires purchases-capacitor v11+ (Capacitor 7).
  // Enable by building with REACT_APP_RC_USE_TEST_STORE=true and REACT_APP_RC_TEST_KEY set.
  const useTestStore =
    process.env.REACT_APP_RC_USE_TEST_STORE === "true" && process.env.REACT_APP_RC_TEST_KEY;
  const apiKey = useTestStore
    ? process.env.REACT_APP_RC_TEST_KEY
    : process.env.REACT_APP_RC_ANDROID_KEY;
  if (process.env.NODE_ENV === "production" && useTestStore) throw new Error("Test Store is disabled in production builds");
  if (!useTestStore && apiKey && !apiKey.startsWith("goog_")) throw new Error("Android billing requires a Google RevenueCat public SDK key");
  if (!apiKey) throw new Error("RevenueCat key not configured (REACT_APP_RC_ANDROID_KEY)");
  if (useTestStore) {
    try {
      const mod = await import("@revenuecat/purchases-capacitor");
      await P.setLogLevel({ level: mod.LOG_LEVEL.DEBUG });
    } catch (e) {
      console.warn("[billing] setLogLevel:", e?.message || e);
    }
  }
  await P.configure({ apiKey });
  configured = true;
  })().catch((error) => { configurationPromise = null; throw error; });
  await configurationPromise;

};

const activePremium = (customerInfo) =>
  Boolean(customerInfo?.entitlements?.active?.premium);

export const createRevenueCatBilling = () => ({
  platform: "android",
  canPurchase: true,
  canSimulate: false,

  async init() {
    await ensureConfigured();
  },

  async identify(appUserID) {
    if (!appUserID) return { ok: false, premium: false };
    await ensureConfigured();
    const P = Purchases;
    const { customerInfo } = await P.logIn({ appUserID: String(appUserID) });
    rememberIdentity(appUserID);
    return { ok: true, premium: activePremium(customerInfo) };
  },

  async clearIdentity() {
    if (!hasRememberedIdentity()) return { ok: true };
    await ensureConfigured();
    const P = Purchases;
    try {
      await P.logOut();
    } catch (e) {
      // RevenueCat can reject logOut when it already considers the user anonymous.
      // Either way, clear our local identified-user marker.
      console.warn("[billing] clearIdentity:", e?.message || e);
    } finally {
      rememberIdentity(null);
    }
    return { ok: true };
  },

  async getPlans() {
    await ensureConfigured();
    const P = Purchases;
    const offerings = await P.getOfferings();
    const pkgs = offerings?.current?.availablePackages || [];
    return pkgs.map((p) => ({
      id: p.product.identifier,
      period: packagePeriod(p),
      title: p.product.title || p.product.identifier,
      priceString: p.product.priceString,
      note: ({
        monthly: "Monthly · Auto-renewing subscription",
        annual: "Annual · Auto-renewing subscription",
        lifetime: "One-time purchase",
      })[packagePeriod(p)] || "Auto-renewing subscription",
      _pkg: p,
    }));
  },

  async isPremium() {
    try {
      await ensureConfigured();
    const P = Purchases;
      const { customerInfo } = await P.getCustomerInfo();
      return activePremium(customerInfo);
    } catch (e) {
      console.warn("[billing] isPremium:", e?.message || e);
      return false;
    }
  },

  async purchase(plan) {
    try {
      await ensureConfigured();
    const P = Purchases;
      let pkg = plan?._pkg;
      if (!pkg) {
        const offerings = await P.getOfferings();
        pkg = (offerings?.current?.availablePackages || []).find(
          (p) => p.product.identifier === (plan?.id || plan)
        );
      }
      if (!pkg) return { ok: false, code: "PRODUCT_NOT_FOUND", message: "This plan isn't available right now." };
      const { customerInfo } = await P.purchasePackage({ aPackage: pkg });
      return { ok: true, premium: activePremium(customerInfo) };
    } catch (e) {
      const msg = String(e?.message || "");
      if (e?.userCancelled || /cancel/i.test(msg)) {
        return { ok: false, code: "CANCELLED", message: "" };
      }
      return { ok: false, code: String(e?.code || "UNKNOWN"), message: "We couldn't confirm the purchase. Check Google Play or restore purchases before trying again." };
    }
  },

  async restore() {
    try {
      await ensureConfigured();
    const P = Purchases;
      const { customerInfo } = await P.restorePurchases();
      return { ok: true, premium: activePremium(customerInfo) };
    } catch (e) {
      return { ok: false, code: String(e?.code || "UNKNOWN"), message: "Couldn't restore just now. Please try again." };
    }
  },
});

